(ns kyber.components.base.button
  (:require [clojure.string :as str]
            [kyber.components.base.text :as text]
            [kyber.components.base.icons :refer [icon-sm
                                                 icon
                                                 icon-lg
                                                 icon-xl
                                                 icon-massive]]
            [kyber.styles.core :refer [styles->classes gs]]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Traditional Buttons
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def base-button-styles
  {:display :flex
   :justify-content :center
   :appearance :none
   :align-items :center
   :height "34px"
   :font-size "12px"
   :line-height (gs [:font-height :body-20])
   :padding [[(gs [:spacing :p8])
              (gs [:spacing :p20])]]
   :text-transform :capitalize
   :cursor :pointer
   :user-select :none
   :transition "all 100ms linear"
   "& span" {:font-size "12px"
             :line-height 0
             :white-space :nowrap
             :font-weight (gs [:font-weight :title-10])}

   (gs [:queries :mobile-landscape])
   {:padding [[(gs [:spacing :p8])
               (gs [:spacing :p16])
               (gs [:spacing :p8])
               (gs [:spacing :p16])]]}})

(def primary-button-styles
  (merge-with
   merge
   base-button-styles
   {:border :none
    :background-color (gs [:colors :primary-lite-600])
    "& span" {:color (gs [:colors :true-white])}
    "&:focus" {:outline (gs [:spacing :p0])
               :border-image :none}
    "&:active" {:background-color (gs [:colors :primary-lite-400])}
    "&:disabled" {:background-color (gs [:colors :gray-scale-200])
                  :cursor :default}

    (gs [:queries :hover])
    {"&:hover" {:background-color (gs [:colors :primary-lite-700])}}}))

(def transparent-button-styles
  (merge-with
   merge
   base-button-styles
   {:background :none
    "& span " {:color (gs [:colors :true-white])}

    "&:active" {:opacity "1"
                :background-color (gs [:colors :true-white])
                "& span" {:color (gs [:colors :primary-lite-600])}}

    "&:disabled" {:background-color (gs [:colors :gray-scale-200])}

    (gs [:queries :hover])
    {"&:hover" {:background-color (gs [:colors :primary-lite-600])}}}))

(def primary-rounded-button-styles
  (merge
   primary-button-styles
   {:border-radius "40px"
    :height "50px"
    :padding (gs [:spacing :p24])
    :box-shadow (gs [:shadows :shadow-12])}))

(def secondary-button-styles
  (merge-with
   merge
   base-button-styles
   {:border :none
    :background (gs [:colors :true-white])
    :box-shadow (str "inset 0 0 0 1px " (gs [:colors :gray-scale-300]))

    "& span"
    {:color (gs [:colors :primary-lite-600])}

    "&:focus"
    {:outline (gs [:spacing :p0])
     :border-image :none}

    "&:active"
    {:background-color (gs [:colors :primary-lite-400])
     :box-shadow (str "inset 0 0 0 1px " (gs [:colors :primary-lite-400]))}

    "&:active span"
    {:color (gs [:colors :true-white])}

    "&:disabled"
    {:box-shadow (str "inset 0 0 0 1px " (gs [:colors :gray-scale-200]))
     :background :none
     :cursor :default}

    "&:disabled span"
    {:color (gs [:colors :gray-scale-300])}

    (gs [:queries :hover])
    {"&:hover"
     {:background-color (gs [:colors :primary-lite-700])
      :box-shadow (str "inset 0 0 0 1px " (gs [:colors :primary-lite-700]))}

     "&:hover span"
     {:color (gs [:colors :true-white])}

     "&:disabled:hover span"
     {:color (gs [:colors :gray-scale-300])}}}))

(def admin-button-styles
  (merge-with
   merge
   base-button-styles
   {:border :none
    :background (gs [:colors :true-white])
    :box-shadow (str "inset 0 0 0 1px " (gs [:colors :error-600]))

    "& span"
    {:color (gs [:colors :error-600])}

    "&:focus"
    {:outline (gs [:spacing :p0])
     :border-image :none}

    "&:active"
    {:background-color (gs [:colors :error-400])
     :box-shadow (str "inset 0 0 0 1px " (gs [:colors :error-400]))}

    "&:active span"
    {:color (gs [:colors :true-white])}

    "&:disabled"
    {:box-shadow (str "inset 0 0 0 1px " (gs [:colors :gray-scale-200]))
     :background :none}

    "&:disabled span"
    {:color (gs [:colors :gray-scale-200])}

    (gs [:queries :hover])
    {"&:hover"
     {:background-color (gs [:colors :error-700])
      :box-shadow (str "inset 0 0 0 1px " (gs [:colors :error-700]))}

     "&:hover span"
     {:color (gs [:colors :true-white])}

     "&:disabled:hover span"
     {:color (gs [:colors :gray-scale-300])}}}))



(def ^{:private true} button-classes
  (styles->classes {:admin-button admin-button-styles
                    :primary-button primary-button-styles
                    :primary-rounded-button primary-rounded-button-styles
                    :secondary-button secondary-button-styles
                    :transparent-button transparent-button-styles}))

(defn- render-button
  [{:keys [on-click class disabled data-test]
    :or {on-click identity
         disabled false}
    :as options}
   copy
   class-name]
  [:button (cond-> (merge options
                          {:on-click on-click
                           :disabled disabled
                           :class [class-name]})
             class (update :class #(if (string? class)
                                     (conj % class)
                                     (into % class)))
             (some? data-test)
             (assoc :data-test data-test))

   [text/body-20 :span copy]])

(defn primary-button
  {:doc "Primary button"
   :id :primary-button
   :version 0
   :variant :a}
  ([copy] [primary-button {} copy])
  ([options copy]
   [render-button options copy (:primary-button button-classes)]))

(defn primary-rounded-button
  {:doc "Primary rounded button"
   :id :rounded-primary-button
   :version 0
   :variant :a}
  ([copy] [primary-rounded-button {} copy])
  ([options copy]
   [render-button options copy (:primary-rounded-button button-classes)]))

(defn secondary-button
  {:doc "Secondary button"
   :id :secondary-button
   :version 0
   :variant :a}
  ([copy] [secondary-button {} copy])
  ([options copy]
   [render-button options copy (:secondary-button button-classes)]))

(defn transparent-button
  {:doc "Transparent button"
   :id :transparent-button
   :version 0
   :variant :a}
  ([copy] [transparent-button {} copy])
  ([options copy]
   [render-button options copy (:transparent-button button-classes)]))

(defn admin-button
  {:doc "Admin button"
   :id :admin-button
   :version 0
   :variant :a}
  ([copy] [admin-button {} copy])
  ([options copy]
   [render-button options copy (:admin-button button-classes)]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Button Link
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def base-button-link-styles
  {:display :flex
   :justify-content :center
   :appearance :none
   :align-items :center
   :padding (gs [:spacing :p0])
   :cursor :pointer
   :user-select :none
   :background :none
   :border :none
   :transition "all 100ms linear"
   :width :fit-content
   "&:disabled span" {:color (gs [:colors :gray-scale-200])}
   "&:focus" {:outline :none}})

(def button-link-styles
  {"& span" {:color (gs [:colors :primary-lite-600])}
   "&:hover span" {:color (gs [:colors :primary-lite-700])}
   "&:active span" {:color (gs [:colors :primary-lite-400])}
   "&:disabled span" {:color (gs [:colors :gray-scale-300])}
   "&:disabled:hover span" {:color (gs [:colors :gray-scale-300])}})

(def admin-button-link-styles
  {"& span" {:color (gs [:colors :error-600])}
   "&:hover span" {:color (gs [:colors :error-700])}
   "&:active span" {:color (gs [:colors :error-400])}
   "&:disabled span" {:color (gs [:colors :gray-scale-300])}
   "&:disabled:hover span" {:color (gs [:colors :gray-scale-300])}})

(def ^{:private true} button-link-classes
  (styles->classes {:button-link (merge base-button-link-styles
                                        button-link-styles)
                    :admin-button-link (merge base-button-link-styles
                                              admin-button-link-styles)
                    ;; Essentially an alias as of right now for admin
                    ;; links
                    :danger-button-link (merge base-button-link-styles
                                               admin-button-link-styles)}))

(defn button-link
  {:doc "Button Link"
   :id :button-link
   :version 0
   :variant :a}
  ([copy] [button-link {:component-el text/body-20} copy])
  ([{:keys [class component-el disabled]
     :or {component-el text/body-20
          disabled false}
     :as options} copy]
   [:button
    (cond-> (-> options
                (assoc :class [(:button-link button-link-classes)])
                (assoc :disabled disabled)
                (dissoc :component-el))
      class (update :class conj class))
    [component-el :span copy]]))

(defn danger-button-link
  {:doc "Danger Button Link"
   :id :danger-button-link
   :version 0
   :variant :a}
  ([copy] [button-link {:component-el text/body-20} copy])
  ([{:keys [class component-el]
     :or {component-el text/body-20}
     :as options} copy]
   [:button
    (cond-> (-> options
                (assoc :class [(:danger-button-link button-link-classes)])
                (dissoc :component-el))
      class (update :class conj class))
    [component-el :span copy]]))

(defn admin-button-link
  {:doc "Admin Button Link"
   :id :admin-button-link
   :version 0
   :variant :a}
  ([copy] [button-link {:component-el text/body-20} copy])
  ([{:keys [class component-el]
     :or {component-el text/body-20}
     :as options} copy]
   [:button
    (cond-> (-> options
                (assoc :class [(:admin-button-link button-link-classes)])
                (dissoc :component-el))
      class (update :class conj class))
    [component-el :span copy]]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Loading Button
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def loading-button-styles
  (merge base-button-styles
         {"&:disabled" {:cursor :auto
                        :background-color (gs [:colors :gray-scale-200])
                        "& i" {:color (gs [:colors :true-white])
                               :margin-right (gs [:spacing :p8])
                               :animation "spin 3s infinite linear"}
                        "& span" {:color (gs [:colors :true-white])}}}))

(def ^{:private true} loading-button-classes
  (styles->classes {:loading-button loading-button-styles}))

(defn loading-button
  {:doc "Loading buttons are used when a request is made to retrieve or store
information without leaving the page. They provide feedback to the user, letting
them know their action is being processed."}
  ([copy] [loading-button {} copy])
  ([{:keys [class]} copy]
   [:button {:disabled true
             :class (str (:loading-button loading-button-classes)
                         (when class
                           (str " " class)))}
    [:span
     [icon :loading]
     [text/body-20 :span copy]]]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Icon Buttons
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def base-icon-button-styles
  {:appearance :none
   :cursor :pointer
   :user-select :none
   :transition "all 100ms linear"
   :border :none
   :padding (gs [:spacing :p8])
   :background :transparent
   "& i" {:color (gs [:colors :primary-lite-600])}
   "&:focus" {:outline (gs [:spacing :p0])
              :border-image :none}
   "&:active i" {:color (gs [:colors :primary-lite-400])}
   "&:disabled i" {:color (gs [:colors :gray-scale-200])}

   (gs [:queries :mobile-landscape])
   {"&:hover i" {:color (gs [:colors :primary-lite-700])}}})

(def base-icon-alt-button-styles
  {:appearance :none
   :cursor :pointer
   :user-select :none
   :transition "all 100ms linear"
   :border :none
   :padding (gs [:spacing :p8])
   :background :none
   :border-radius (gs [:radius :r3])
   "& i" {:color (gs [:colors :primary-lite-600])}
   "&:focus" {:outline (gs [:spacing :p0])
              :border-image :none}
   "&:active i" {:color (gs [:colors :primary-lite-400])}
   "&:disabled i" {:color (gs [:colors :gray-scale-200])}

   (gs [:queries :hover])
   {"&:hover i" {:color (gs [:colors :primary-lite-700])}
    "&:hover:disabled i" {:color (gs [:colors :gray-scale-200])}}})

(def small-icon-button-styles
  base-icon-button-styles)

(def small-icon-alt-button-styles
  base-icon-alt-button-styles)

(def icon-button-styles
  base-icon-button-styles)

(def icon-alt-button-styles
  base-icon-alt-button-styles)

(def large-icon-button-styles
  base-icon-button-styles)

(def large-icon-alt-button-styles
  base-icon-alt-button-styles)

(def x-large-icon-button-styles
  base-icon-button-styles)

(def x-large-icon-alt-button-styles
  base-icon-alt-button-styles)

(def massive-icon-button-styles
  base-icon-button-styles)

(def massive-icon-alt-button-styles
  base-icon-alt-button-styles)

(def ^{:private true} icon-button-classes
  (styles->classes {:small small-icon-button-styles
                    :small-alt small-icon-alt-button-styles
                    :default icon-button-styles
                    :default-alt icon-alt-button-styles
                    :large large-icon-button-styles
                    :large-alt large-icon-alt-button-styles
                    :x-large x-large-icon-button-styles
                    :x-large-alt x-large-icon-alt-button-styles
                    :massive massive-icon-button-styles
                    :massive-alt massive-icon-alt-button-styles}))

(def ^{:private true} icon-component-map
  {:small icon-sm
   :small-alt icon-sm
   :default icon
   :default-alt icon
   :large icon-lg
   :large-alt icon-lg
   :x-large icon-xl
   :x-large-alt icon-xl
   :massive icon-massive
   :massive-alt icon-massive})

(def ^{:private true} icon-label-map
  {:arrow-right "Go forward"
   :arrow-right-simple "Go forward"
   :bars "Menu"
   :bookmark "Bookmark"
   :bookmark-solid "Bookmark"
   :bold "Bold"
   :bold-light "Bold"
   :camera "Camera"
   :check "Confirm"
   :check-regular "Confirm"
   :check-circle "Confirmed"
   :check-circle-empty "Select correct"
   :check-square "Checked"
   :check-square-empty "Unchecked"
   :chevron-left "Previous"
   :chevron-right "Next"
   :chevron-down "Expand"
   :chevron-up "Collapse"
   :circle "Select"
   :circle-fill "Selected"
   :close "Close"
   :cog "Settings"
   :cogs "Settings"
   :columns "Columns"
   :comment "Comment"
   :compress "Compress"
   :delete "Delete"
   :download "Download"
   :edit "Edit"
   :expand "Expand"
   :external-link "Open in new window"
   :eye "View"
   :heading "Heading"
   :heading-light "Heading"
   :heading-1 "Heading 1"
   :heading-1-light "Heading 1"
   :heading-2 "Heading 2"
   :heading-2-light "Heading 2"
   :heading-3 "Heading 3"
   :heading-3-light "Heading 3"
   :help "Help"
   :highlighter "Highlight"
   :images "Images"
   :info "Info"
   :italic "Italic"
   :italic-light "Italic"
   :list "Bullet list"
   :list-light "Bullet list"
   :list-ordered "Numbered list"
   :list-ordered-light "Numbered list"
   :list-square "List"
   :loading "Loading"
   :minus "Remove"
   :minus-circle "Remove"
   :pause "Pause"
   :pause-circle "Pause"
   :pin "Pin"
   :play "Play"
   :play-circle "Play"
   :play-circle-light "Play"
   :plus "Add"
   :plus-circle "Add"
   :reload "Reload"
   :rows "Rows"
   :search "Search"
   :settings "Settings"
   :sort-up "Sort ascending"
   :sort-down "Sort descending"
   :star "Star"
   :star-filled "Starred"
   :subscript "Subscript"
   :subscript-light "Subscript"
   :superscript "Superscript"
   :superscript-light "Superscript"
   :table "Table"
   :times "Close"
   :underline "Underline"
   :underline-light "Underline"
   :undo "Undo"
   :vector-square "Vector"
   :volume-off "Mute"
   :volume-up "Volume"
   :warning "Warning"})

(defn- icon-name->label
  [icon-name]
  (or (get icon-label-map icon-name)
      (str/capitalize (str/replace (name icon-name) #"-" " "))))

(defn- render-icon-button
  [icon-type
   {:keys [on-click class style disabled on-mouse-down]
    :or {on-click identity
         on-mouse-down identity
         disabled false
         style {}} :as options}
   icon-name]
  [:button (merge {:aria-label (icon-name->label icon-name)}
                  options
                  {:on-mouse-down on-mouse-down
                   :on-click on-click
                   :disabled disabled
                   :class (str (icon-type icon-button-classes)
                               (when class (str " " class)))
                   :style style})
   [(icon-type icon-component-map) icon-name]])

(defn icon-button-sm
  {:doc "Icon button small"
   :id :icon-button-sm
   :version 0
   :variant :a}
  ([icon-name] [icon-button-sm {} icon-name])
  ([options icon-name]
   [render-icon-button :small options icon-name]))

(defn icon-button-alt-sm
  {:doc "Icon (Alt) button small"
   :id :icon-button-alt-sm
   :version 0
   :variant :a}
  ([icon-name] [icon-button-alt-sm {} icon-name])
  ([options icon-name]
   [render-icon-button :small-alt options icon-name]))

(defn icon-button
  {:doc "Icon button default"
   :id :icon-button
   :version 0
   :variant :a}
  ([icon-name] [icon-button {} icon-name])
  ([options icon-name]
   [render-icon-button :default options icon-name]))

(defn icon-button-alt
  {:doc "Icon (Alt) button default"
   :id :icon-button-alt
   :version 0
   :variant :a}
  ([icon-name] [icon-button-alt {} icon-name])
  ([options icon-name]
   [render-icon-button :default-alt options icon-name]))

(defn icon-button-lg
  {:doc "Icon button large"
   :id :icon-button-lg
   :version 0
   :variant :a}
  ([icon-name] [icon-button-lg {} icon-name])
  ([options icon-name]
   [render-icon-button :large options icon-name]))

(defn icon-button-alt-lg
  {:doc "Icon (Alt) button large"
   :id :icon-button-alt-lg
   :version 0
   :variant :a}
  ([icon-name] [icon-button-alt-lg {} icon-name])
  ([options icon-name]
   [render-icon-button :large-alt options icon-name]))

(defn icon-button-xl
  {:doc "Icon button x-large"
   :id :icon-button-xl
   :version 0
   :variant :a}
  ([icon-name] [icon-button-xl {} icon-name])
  ([options icon-name]
   [render-icon-button :x-large options icon-name]))

(defn icon-button-alt-xl
  {:doc "Icon (Alt) x-large"
   :id :icon-button-alt-xl
   :version 0
   :variant :a}
  ([icon-name] [icon-button-alt-xl {} icon-name])
  ([options icon-name]
   [render-icon-button :x-large-alt options icon-name]))

(defn icon-button-massive
  {:doc "Icon button massive"
   :id :icon-button-massive
   :version 0
   :variant :a}
  ([icon-name] [icon-button-massive {} icon-name])
  ([options icon-name]
   [render-icon-button :massive options icon-name]))

(defn icon-button-alt-massive
  {:doc "Icon (Alt) massive"
   :id :icon-button-alt-massive
   :version 0
   :variant :a}
  ([icon-name] [icon-button-alt-massive {} icon-name])
  ([options icon-name]
   [render-icon-button :massive-alt options icon-name]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Button Groups
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(def button-group-styles
  {:display :flex
   "& button:first-child"
   {:margin-right (gs [:spacing :p1])
    :border-radius [[(gs [:radius :r3])
                     (gs [:radius :r0])
                     (gs [:radius :r0])
                     (gs [:radius :r3])]]}
   "& button:last-child"
   {:border-radius [[(gs [:radius :r0])
                     (gs [:radius :r3])
                     (gs [:radius :r3])
                     (gs [:radius :r0])]]}})

(def ^{:private true} button-group-classes
  (styles->classes {:button-group button-group-styles}))

(defn- render-button-group
  [{:keys [left-button right-button class]} icon-size]
  [:div {:class (str (:button-group button-group-classes)
                     (when class (str " " class)))}
   [render-icon-button
    icon-size
    (or (:options left-button) {})
    (:icon left-button)]
   [render-icon-button
    icon-size
    (or (:options right-button) {})
    (:icon right-button)]])

(defn button-group-sm
  {:doc "Button group small"}
  [options]
  [render-button-group options :small])

(defn button-group
  {:doc "Button group"}
  [options]
  [render-button-group options :default])

(defn button-group-lg
  {:doc "Button group large"}
  [options]
  [render-button-group options :large])
